import {
  BaseQuestionIdentifier,
  BaseQuestionResource,
  DisclosureSource,
  QuestionType,
} from "../types/BaseQuestionResource";

import { PayloadAction } from "@reduxjs/toolkit";
import { QuestionPageResource } from "../types/QuestionPageResource";
import { QuestionSectionResource } from "../types/QuestionSectionResource";
import merge from "lodash.merge";

/**
 * Check if basequestion has any cancelled disclosures.
 * @param baseQuestion A base question object.
 */
const hasCancelledPicklistDisclosures = (
  baseQuestion: BaseQuestionResource,
) => {
  return (
    baseQuestion.disclosureSource &&
    baseQuestion.disclosureSource === DisclosureSource.PICKLIST &&
    baseQuestion.answerValue === "false"
  );
};

/**
 * Show or hide conditional questions triggered by question of type CONDITIONAL.
 * Backend does not return updated question here, so we need to do it by hand.
 * @param baseQuestions List of basequestions.
 */
export const updateConditionalQuestions = (
  baseQuestions: BaseQuestionResource[],
) => {
  const conditionalQuestionsToShow: BaseQuestionIdentifier[] = [];
  const conditionalQuestionsToHide: BaseQuestionIdentifier[] = [];

  // Get all conditional questions that need updating.
  baseQuestions.forEach((baseQuestion) => {
    if (
      baseQuestion.questionType === QuestionType.CONDITIONAL &&
      baseQuestion.conditionalQuestionMeta
    ) {
      if (baseQuestion.questionsImpactedByAnswerChange) {
        if (!baseQuestion.answerRequiresAction) {
          baseQuestion.questionsImpactedByAnswerChange.forEach((q) => {
            conditionalQuestionsToHide.push(q);
          });
        } else {
          baseQuestion.questionsImpactedByAnswerChange.forEach((q) => {
            conditionalQuestionsToShow.push(q);
          });
        }
      }
    }
  });

  // Map through questions and show/hide based on child question codes.
  return baseQuestions.map((baseQuestion) => {
    if (conditionalQuestionsToShow) {
      const shouldShow =
        conditionalQuestionsToShow.filter(
          (conditional) => conditional.baseQuestionCode === baseQuestion.code,
        ).length > 0;

      if (shouldShow) {
        baseQuestion.visible = true;
      }
    }

    if (conditionalQuestionsToHide) {
      const shouldHideConditionalQuestion =
        conditionalQuestionsToHide.filter(
          (q) => q.baseQuestionCode === baseQuestion.code,
        ).length > 0;

      if (shouldHideConditionalQuestion) {
        baseQuestion.visible = false;

        // Reset answers
        baseQuestion.answerValue = undefined;
        baseQuestion.answerText = undefined;

        // Remove any disclosures.
        if (baseQuestion.disclosures) {
          baseQuestion.disclosures = undefined;
        }
      }
    }

    return baseQuestion;
  });
};

/**  Update base question in state.
 *   First map() merges response from backend, then the second map()
 *   toggles any conditional questions affected by update.
 *
 *   @param {QuestionPageResource[]} pages Current pages slice in state.
 *   @param {PayloadAction<BaseQuestionResource>} action The action returned from the saga.
 *   @return {QuestionPageResource[]} The updated pages slice with new base question merged.
 */
export const mergeUpdatedBaseQuestion = (
  pages: QuestionPageResource[],
  action: PayloadAction<BaseQuestionResource>,
) => {
  return pages
    .map((page: QuestionPageResource) => {
      return {
        ...page,
        sections: page.sections.map((section: QuestionSectionResource) => {
          return {
            ...section,
            baseQuestions: section.baseQuestions.map((baseQuestion) => {
              if (baseQuestion.code === action.payload.code) {
                // Merge updated question with existing.
                const updatedBaseQuestion = merge(baseQuestion, action.payload);

                // Remove any cancelled picklist disclosures from updated question.
                if (hasCancelledPicklistDisclosures(updatedBaseQuestion)) {
                  updatedBaseQuestion.disclosures = undefined;
                }

                return updatedBaseQuestion;
              } else {
                return baseQuestion;
              }
            }),
          };
        }),
      };
    })
    .map((page: QuestionPageResource) => {
      return {
        ...page,
        sections: page.sections.map((section) => {
          return {
            ...section,
            baseQuestions: updateConditionalQuestions(section.baseQuestions),
          };
        }),
      };
    });
};
